'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: number;
  question: string;
  answer: string;
  category: 'process' | 'pricing' | 'timeline' | 'legal';
}

interface FAQSectionProps {
  className?: string;
}

const FAQSection = ({ className = '' }: FAQSectionProps) => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(1);

  const faqs: FAQ[] = [
    {
      id: 1,
      question: 'How does the cash offer process work?',
      answer:
        'Our process is simple and transparent. First, you submit your property information through our secure form. Within 24-48 hours, our team evaluates your property using local market data and condition assessments. We then present you with a fair, no-obligation cash offer that includes a detailed breakdown of how we arrived at the price. If you accept, we can close in 30 days or less.',
      category: 'process',
    },
    {
      id: 2,
      question: 'How do you determine the offer price?',
      answer:
        'We use a comprehensive evaluation process that considers current market value, property condition, location, recent comparable sales, and repair costs. Unlike other cash buyers who use generic formulas, we conduct thorough local market analysis to ensure our offers are fair and competitive. We provide a detailed breakdown showing exactly how we calculated your offer.',
      category: 'pricing',
    },
    {
      id: 3,
      question: 'Are there any fees or hidden costs?',
      answer:
        'No, there are absolutely no fees, commissions, or hidden costs when you sell to Silk Solutions. Unlike traditional real estate sales that involve agent commissions (typically 6%), closing costs, and repair expenses, our cash offers are net to you. The price we quote is exactly what you receive at closing.',
      category: 'pricing',
    },
    {
      id: 4,
      question: 'How quickly can you close?',
      answer:
        'We can typically close in 30 days or less, depending on your timeline and any title issues. Since we buy with cash, there are no financing delays or appraisal contingencies. If you need to move quickly due to foreclosure or other urgent circumstances, we will be direct about what timeline is realistic. Conversely, if you need more time to move, we can work with your schedule.',
      category: 'timeline',
    },
    {
      id: 5,
      question: 'Do I need to make any repairs before selling?',
      answer:
        'No repairs are necessary. We buy houses in any condition - from move-in ready to completely distressed properties. Whether your home needs minor cosmetic updates or major structural repairs, we factor the condition into our offer and handle all repairs after closing. This saves you time, money, and the hassle of managing contractors.',
      category: 'process',
    },
    {
      id: 6,
      question: 'What types of properties do you buy?',
      answer:
        'We purchase all types of residential properties including single-family homes, townhouses, condominiums, multi-family properties, and even mobile homes. We buy properties in any condition, from any situation - foreclosure, divorce, inheritance, financial hardship, or simply wanting a quick sale. Location and property type are rarely barriers to making an offer.',
      category: 'process',
    },
    {
      id: 7,
      question: 'Is this a legitimate business transaction?',
      answer:
        'Yes, Silk Solutions is a fully licensed and insured real estate investment company. We are members of the Better Business Bureau and comply with all local and state regulations. All transactions are handled through licensed title companies or real estate attorneys, ensuring proper legal documentation and protection for both parties. We can provide references and proof of previous successful transactions.',
      category: 'legal',
    },
    {
      id: 8,
      question: 'What happens if I change my mind?',
      answer:
        'Our cash offers are completely no-obligation. You can decline our offer at any time without penalty or pressure. Even after accepting our offer, you typically have a few days during the contract review period to change your mind. We believe in transparent, pressure-free transactions where you feel completely comfortable with your decision.',
      category: 'legal',
    },
    {
      id: 9,
      question: 'How is this different from listing with a real estate agent?',
      answer:
        'The main differences are speed, convenience, and cost. With an agent, you pay 6% commission, make repairs, stage the home, deal with showings, and wait 30-60+ days hoping for a qualified buyer. With us, you get a direct cash offer in days, no repairs needed, no showings, no commissions, and closing in 30 days or less. You trade maximum price for speed and convenience.',
      category: 'process',
    },
    {
      id: 10,
      question: "Can you help if I'm facing foreclosure?",
      answer:
        "Yes, we specialize in helping homeowners avoid foreclosure. Time is critical in foreclosure situations, which is why our fast closing process is ideal. We can often close before the foreclosure sale date, allowing you to avoid the foreclosure on your credit report and potentially walk away with cash instead of losing everything. Contact us immediately if you're facing foreclosure - every day counts.",
      category: 'timeline',
    },
  ];

  const categories = [
    { key: 'process', label: 'Process', icon: 'CogIcon' },
    { key: 'pricing', label: 'Pricing', icon: 'CurrencyDollarIcon' },
    { key: 'timeline', label: 'Timeline', icon: 'ClockIcon' },
    { key: 'legal', label: 'Legal', icon: 'ScaleIcon' },
  ];

  const toggleFAQ = (id: number) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const filteredFAQs = (category: string) => {
    return faqs.filter((faq) => faq.category === category);
  };

  return (
    <section className={`py-16 lg:py-24 bg-surface ${className}`}>
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Get answers to common questions about our cash home buying process. Don't see your
            question? Contact us directly for personalized assistance.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => setOpenFAQ(null)}
              className="flex items-center space-x-2 px-4 py-2 bg-card hover:bg-primary/5 border border-border rounded-lg silk-smooth"
            >
              <Icon name={category.icon as any} size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">{category.label}</span>
            </button>
          ))}
        </div>

        {/* FAQ Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {categories.map((category) => (
            <div key={category.key} className="space-y-4">
              <h3 className="text-xl font-heading font-semibold text-foreground mb-6 flex items-center">
                <Icon name={category.icon as any} size={20} className="text-primary mr-2" />
                {category.label} Questions
              </h3>

              {filteredFAQs(category.key).map((faq) => (
                <div
                  key={faq.id}
                  className="bg-card rounded-lg border border-border shadow-card overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/50 silk-smooth"
                  >
                    <span className="font-medium text-foreground pr-4">{faq.question}</span>
                    <Icon
                      name={openFAQ === faq.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                      size={20}
                      className="text-muted-foreground flex-shrink-0"
                    />
                  </button>

                  {openFAQ === faq.id && (
                    <div className="px-6 pb-4 border-t border-border">
                      <p className="text-muted-foreground leading-relaxed pt-4">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Still Have Questions CTA */}
        <div className="text-center mt-16 p-8 bg-card rounded-xl border border-border shadow-card">
          <Icon name="QuestionMarkCircleIcon" size={48} className="text-primary mx-auto mb-4" />
          <h3 className="text-2xl font-heading font-bold text-foreground mb-4">
            Still Have Questions?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Our team is here to help. Get personalized answers to your specific situation and learn
            how we can help you achieve your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 silk-smooth shadow-button"
            >
              <Icon name="PhoneIcon" size={20} className="mr-2" />
              Call Us Now
            </a>
            <a
              href="#property-form"
              className="inline-flex items-center justify-center px-6 py-3 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 silk-smooth shadow-button trust-glow"
            >
              <Icon name="DocumentTextIcon" size={20} className="mr-2" />
              Get Your Offer
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
